import React, {forwardRef, ReactNode, Ref, useCallback, useMemo} from "react";
import {MuiRFF, useForm} from "../../form";
import {TextField, TextFieldProps} from "@mui/material";
import InputMask, {Props} from "react-input-mask";
import {FieldProps} from "react-final-form";


type MuiPhoneTextFieldProps = TextFieldProps & {
    form: false;
}

type FormPhoneTextFieldProps = MuiRFF.TextFieldProps & {
    form: true;
}

type PhoneTextFieldTypes = "MuiRFF" | "Mui";


export type PhoneTextFieldProps<T extends PhoneTextFieldTypes = "Mui"> =
    T extends "MuiRFF"
        ? FormPhoneTextFieldProps
        : T extends "Mui"
            ? MuiPhoneTextFieldProps
            : never;

const _PhoneTextfield = <T extends PhoneTextFieldTypes = "Mui">(props: PhoneTextFieldProps<T>, ref: Ref<HTMLInputElement>) => {
    const form = useForm();
    const {onBlur, onChange} = props as unknown as PhoneTextFieldProps;
    const inputMaskProps = useMemo<Omit<Props, 'mask'>>(() => ({
        ...props,
        form: undefined,
        onChange: undefined,
        onBlur: undefined,
    } as unknown as Props), [props])

    /**
     * Custom error function that only shows error when form has been submitted and not changed
     */
    const customShowErrorFunction = useCallback<MuiRFF.ShowErrorFunc>(({meta: {error, submitFailed}}) => {
        return (submitFailed && error);
    }, [])

    /**
     * Formats the value of the phone input.
     */
    const formatValue = useCallback<Required<FieldProps<any, any>>['format']>((val) => {
        return (val?.trim() ?? '')
            .replace(/[()_-]/g, '')
            .replace(/\+1/g, '')
            .replaceAll(' ', '')
    }, [])

    return (
        <>
            {
                props.form &&
                <InputMask
                    {...inputMaskProps}
                    value={(form?.getFieldState(props.name)?.value ?? "")}
                    onChange={(e) => form?.change(e.target.name, e.target.value)}
                    maskChar={'_'}
                    mask={"+\\1 (999) 999-9999"}
                >
                    {((inputProps: TextFieldProps) =>
                            <MuiRFF.TextField
                                name={props.name}
                                variant={'outlined'}
                                showError={customShowErrorFunction}
                                fieldProps={{formatOnBlur: true, format: formatValue}}
                                {...inputProps}
                                ref={ref}
                                type={'tel'}
                            />
                    ) as unknown as ReactNode}
                </InputMask>
            }
            {
                !props.form &&
                <InputMask
                    {...inputMaskProps}
                    value={props.value as string}
                    onChange={(e) => onChange?.(e)}
                    onBlur={(e) => onBlur?.({
                        ...e,
                        target: {...e.target, value: !e.target.value ? e.target.value : formatValue(e.target.value.toString(), '')}
                    })}
                    maskChar={'_'}
                    mask={"+\\1 (999) 999-9999"}
                >
                    {((inputProps: TextFieldProps) =>
                            <TextField
                                variant={'outlined'}
                                type={props.type}
                                {...inputProps}
                                ref={ref}
                            />
                    ) as unknown as ReactNode}
                </InputMask>
            }
        </>
    )
}

const PhoneTextfield = forwardRef(_PhoneTextfield)

export default PhoneTextfield;
